import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/Button';
import { Plus } from 'lucide-react';
import type { EmployeeInfo } from '@momentum/types';
import type { DisplayEmployeeWithRole } from '../shared/types/one-on-one.types';
import { MeetingCard } from './MeetingCard';
import { useGetProfessionalMeetingsAsEmployee } from '@/shared/hooks/useOneOnOne';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { groupProfessionalMeetings } from '../shared/utils/group-professional-meetings';

interface ProfessionalMeetingsAsEmployeeProps {
  displayEmployee: DisplayEmployeeWithRole | null;
  onSelectEmployee: (employee: DisplayEmployeeWithRole) => void;
  allEmployees: EmployeeInfo[];
  onCreateMeetingWithCoworker?: () => void;
}

export const ProfessionalMeetingsAsEmployee = ({
  displayEmployee,
  onSelectEmployee,
  allEmployees,
  onCreateMeetingWithCoworker,
}: ProfessionalMeetingsAsEmployeeProps) => {
  const { t } = useTranslation('oneOnOne');
  const currentEmployee = useEmployeeContext();

  const { data: professionalMeetingsAsEmployeeData = [] } =
    useGetProfessionalMeetingsAsEmployee({
      employeeId: currentEmployee._id,
    });

  const professionalMeetings = useMemo(
    () =>
      groupProfessionalMeetings(
        professionalMeetingsAsEmployeeData,
        allEmployees,
        'managerId'
      ),
    [professionalMeetingsAsEmployeeData, allEmployees]
  );

  const hasMeetings = professionalMeetings && professionalMeetings.length > 0;

  return (
    <>
      <div className="flex justify-center py-6">
        <div className="w-[90%] border-t border-slate-200" />
      </div>

      <div className="px-6 pb-2">
        <h3 className="mb-4 text-xs font-medium uppercase tracking-wide text-slate-500">
          {t('professionalMeetings')}
        </h3>
        {hasMeetings ? (
          <div className="space-y-3">
            {professionalMeetings.map((profMeeting) => {
              const isSelected =
                displayEmployee?._id === profMeeting.employeeId;

              const meetingsWithDates =
                profMeeting.meetings?.filter(
                  (meeting) => meeting.schedualedDate
                ) || [];

              const lastMeetingDate =
                meetingsWithDates.length > 0
                  ? meetingsWithDates.reduce((latest, meeting) => {
                      if (!meeting.schedualedDate) return latest;
                      const meetingDate = new Date(meeting.schedualedDate);
                      const latestDate = latest
                        ? new Date(latest)
                        : new Date(0);
                      return meetingDate > latestDate
                        ? meeting.schedualedDate
                        : latest;
                    }, null as string | null)
                  : null;

              return (
                <MeetingCard
                  key={profMeeting.employeeId}
                  employeeName={profMeeting.employeeName}
                  employeeImageUrl={profMeeting.employeeImageUrl}
                  lastMeetingDate={lastMeetingDate}
                  isSelected={isSelected}
                  onSelect={() => {
                    const employee = allEmployees.find(
                      (e) => e._id === profMeeting.employeeId
                    );

                    if (employee) {
                      onSelectEmployee({
                        _id: employee._id,
                        firstName: employee.personalInfo.firstName,
                        lastName: employee.personalInfo.lastName,
                        imageUrl: employee.personalInfo.imageUrl,
                        role: employee.role,
                        managerId: employee.workInfo.managerId,
                        lastMeetingDate: lastMeetingDate || undefined,
                        daysSinceLastMeeting: undefined,
                        allMeetings: profMeeting.meetings,
                        meetingRole: 'employee',
                      });
                    }
                  }}
                />
              );
            })}
          </div>
        ) : null}
        {onCreateMeetingWithCoworker && (
          <Button
            onClick={onCreateMeetingWithCoworker}
            className="mt-4 w-full h-10 gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('createProfessionalMeeting')}
          </Button>
        )}
      </div>
    </>
  );
};
